import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Hand } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

export default function IdleScreensaver() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [addons, setAddons] = useState([]);
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
  }, []);

  const enabled = settings ? settings.screensaver_enabled !== false : false;
  const minutes = settings?.idle_minutes ?? 3;
  const isKiosk = typeof window !== "undefined" && window.innerWidth >= 768;

  const homeSlide = settings ? {
    id: "__home__",
    kind: "home",
    image_url: settings.screensaver_image || settings.hero_image || "",
    imageOnly: !!settings.screensaver_image,
    logo_url: settings.logo_url || "",
    title: settings.property_name || "",
    description: settings.welcome_message || settings.tagline || "",
    target: "/",
  } : null;
  const slides = [homeSlide, ...addons.map((a) => ({ ...a, kind: "addon", target: "/add-ons" }))].filter(Boolean);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!enabled || !isKiosk || slides.length === 0) return;
    timerRef.current = setTimeout(() => setActive(true), Math.max(1, minutes) * 60 * 1000);
  }, [enabled, isKiosk, slides.length, minutes]);

  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];
    const onActivity = () => { if (!active) resetTimer(); };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resetTimer, active]);

  useEffect(() => {
    if (!active || slides.length < 2) return;
    setIdx(0);
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 8000);
    return () => clearInterval(t);
  }, [active, slides.length]);

  const dismiss = () => { setActive(false); resetTimer(); navigate("/"); };

  if (!active || slides.length === 0) return null;
  const a = slides[idx] || slides[0];
  const isHome = a.kind === "home";
  const imageOnly = !!a.imageOnly;
  const isVideo = !!a.video_url;
  const rawTitle = a.title || "";
  const parenM = /^(.*?)\s*(\([^)]*\))\s*$/.exec(rawTitle);
  const handedM = /^(.*?)\s*\(?\s*(left|right)[-\s]?handed\s*\)?$/i.exec(rawTitle);
  let titleMain = rawTitle;
  let titleParen = "";
  if (parenM) { titleMain = parenM[1]; titleParen = parenM[2]; }
  else if (handedM) { titleMain = handedM[1]; const s = handedM[2]; titleParen = `(${s.charAt(0).toUpperCase()}${s.slice(1).toLowerCase()} handed)`; }

  return (
    <div
      data-testid="idle-screensaver"
      onClick={dismiss}
      onTouchStart={dismiss}
      className="fixed inset-0 z-[100] bg-brand-forest overflow-hidden cursor-pointer select-none"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={a.id}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.9 }}
          className="absolute inset-0"
        >
          {isVideo ? (
            <video src={mediaUrl(a.video_url)} autoPlay loop muted playsInline data-testid="idle-video"
              className="absolute inset-0 h-full w-full object-cover" />
          ) : a.image_url ? (
            <motion.img src={mediaUrl(a.image_url)} alt={a.title}
              initial={{ scale: 1 }} animate={{ scale: imageOnly ? 1 : 1.04 }} transition={{ duration: 10, ease: "linear" }}
              className={`absolute inset-0 h-full w-full ${imageOnly ? "object-cover" : isHome ? "object-cover" : "object-contain p-8 sm:p-16"}`} />
          ) : (
            <div className="absolute inset-0 bg-brand-forest" />
          )}
          {!imageOnly && <div className="absolute inset-0 bg-gradient-to-t from-brand-forest via-brand-forest/60 to-brand-forest/10" />}
        </motion.div>
      </AnimatePresence>

      <div className="relative h-full w-full flex flex-col justify-end p-10 sm:p-16 lg:p-24">
        {!imageOnly && (
        <motion.div
          key={`text-${a.id}`}
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="max-w-3xl"
        >
          <span className="overline text-brand-terracotta/90 mb-4 block">{isHome ? (settings?.tagline || "Your Stay Guidebook") : "Available While You Stay"}</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight text-white leading-none mb-5">
            {titleMain}
            {titleParen && <span className="block text-2xl sm:text-3xl text-white/80 mt-3 font-medium tracking-normal">{titleParen}</span>}
          </h1>
          {a.description && (
            <p className="text-white/85 text-lg sm:text-xl max-w-2xl leading-relaxed mb-6">{a.description}</p>
          )}
          <div className="flex items-center gap-5">
            {!isHome && a.price_note && (
              <span className="text-2xl sm:text-3xl font-display text-brand-terracotta">{a.price_note}</span>
            )}
            <span className="inline-flex items-center gap-2.5 rounded-full bg-white text-brand-forest px-6 py-3 text-base font-semibold shadow-lg animate-pulse">
              <Hand size={20} className="text-brand-terracotta" /> {isHome ? "Tap to open the guidebook" : "Tap anywhere to explore"}
            </span>
          </div>
        </motion.div>
        )}

        {slides.length > 1 && (
          <div className="absolute top-10 right-10 flex gap-2" data-testid="idle-dots">
            {slides.map((_, i) => (
              <span key={i} className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-brand-terracotta" : "w-2 bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
